// Pure (no React/DOM) JSON-LD graph builders + the stable @id registry for
// the whole site. Kept dependency-free so scripts/verify/verify-schema.js can
// import and validate these functions directly in Node, without a browser.
//
// Architecture note: this is a client-rendered SPA served from static
// hosting -- there is no shared "already loaded" document a crawler can
// carry entity definitions over from. Every route is fetched and rendered
// independently, so the sitewide entities (Organization/WebSite/Person/logo)
// are re-embedded in full, with the *same* @id, on every single page's
// @graph -- not defined once on Home and referenced by bare @id elsewhere.
// Page-specific nodes (WebPage/CollectionPage/CreativeWork/Service/...)
// reference the sitewide entities by @id since they live in the same graph.
import { DOMAIN, SITE, FOUNDER, canonicalUrl } from './site';

export { canonicalUrl };

export const ids = {
  organization: `${DOMAIN}/#organization`,
  website: `${DOMAIN}/#website`,
  person: `${DOMAIN}/#person`,
  logo: `${DOMAIN}/#logo`,
  webpage: (path) => `${canonicalUrl(path)}#webpage`,
  breadcrumb: (path) => `${canonicalUrl(path)}#breadcrumb`,
  itemList: (path) => `${canonicalUrl(path)}#itemlist`,
  faq: (path) => `${canonicalUrl(path)}#faq`,
  project: (idx) => `${DOMAIN}/project/${idx}#creativework`,
  projectPage: (idx) => `${DOMAIN}/project/${idx}#webpage`,
  service: (slug) => `${DOMAIN}/package/${slug}#service`,
  servicePage: (slug) => `${DOMAIN}/package/${slug}#webpage`,
};

// Recursively strips undefined/null/empty-string/empty-array/empty-object
// values so builders can pass through optional data (e.g. a missing
// description) without hand-guarding every field, and so the rendered
// <script> never contains "undefined" or "null" literals.
export function prune(value) {
  if (Array.isArray(value)) {
    const arr = value.map(prune).filter((v) => v !== undefined);
    return arr.length ? arr : undefined;
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      const pruned = prune(val);
      if (pruned !== undefined) out[key] = pruned;
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (value === '' || value === undefined || value === null) return undefined;
  return value;
}

const ref = (id) => ({ '@id': id });

export function buildLogoImage() {
  return {
    '@type': 'ImageObject',
    '@id': ids.logo,
    url: SITE.logo.url,
    contentUrl: SITE.logo.url,
    width: SITE.logo.width,
    height: SITE.logo.height,
  };
}

export function buildPerson() {
  return {
    '@type': 'Person',
    '@id': ids.person,
    name: FOUNDER.name,
    jobTitle: FOUNDER.jobTitle,
    worksFor: ref(ids.organization),
  };
}

export function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': ids.organization,
    name: SITE.name,
    url: DOMAIN,
    description: SITE.description,
    logo: ref(ids.logo),
    image: ref(ids.logo),
    email: SITE.email,
    telephone: SITE.telephone,
    sameAs: SITE.sameAs,
    areaServed: SITE.areaServed,
    knowsLanguage: SITE.inLanguages,
    founder: ref(ids.person),
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: SITE.email,
      telephone: SITE.telephone,
      areaServed: SITE.areaServed,
      availableLanguage: SITE.inLanguages,
    },
  };
}

export function buildWebsite({ lang }) {
  return {
    '@type': 'WebSite',
    '@id': ids.website,
    url: DOMAIN,
    name: SITE.name,
    publisher: ref(ids.organization),
    inLanguage: lang,
  };
}

// The 4 sitewide nodes, identical (same @id, same content) on every page's
// graph -- only `lang` (WebSite.inLanguage) varies between pages.
export function buildBaseGraph({ lang }) {
  return [
    buildOrganization(),
    buildWebsite({ lang }),
    buildPerson(),
    buildLogoImage(),
  ];
}

export function buildWebPage({
  path,
  name,
  description,
  lang,
  type = 'WebPage',
  about = ref(ids.organization),
  primaryImage,
  mainEntity,
}) {
  return {
    '@type': type,
    '@id': ids.webpage(path),
    url: canonicalUrl(path),
    name,
    description,
    isPartOf: ref(ids.website),
    about,
    mainEntity,
    primaryImageOfPage: primaryImage,
    inLanguage: lang,
  };
}

// items: [{ name, path }, ...] starting with Home, ending with the current
// page. Every position (including the last) gets an `item` URL -- one
// consistent style throughout, per Sprint 7 section 9.
export function buildBreadcrumb({ path, items }) {
  return {
    '@type': 'BreadcrumbList',
    '@id': ids.breadcrumb(path),
    itemListElement: items.map((entry, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: entry.name,
      item: canonicalUrl(entry.path),
    })),
  };
}

// items: [{ name, url, id? }, ...] in real, currently-rendered order.
// When `id` is given (the entry has its own JSON-LD entity elsewhere in
// this graph, e.g. a project's CreativeWork or a package's Service), the
// ListItem links to it by @id rather than repeating a bare URL.
export function buildItemList({ path, items }) {
  return {
    '@type': 'ItemList',
    '@id': ids.itemList(path),
    itemListElement: items.map((entry, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: entry.name,
      item: entry.id ? ref(entry.id) : entry.url,
    })),
  };
}

export function buildFAQPage({ path, lang, items }) {
  return {
    '@type': 'FAQPage',
    '@id': ids.faq(path),
    inLanguage: lang,
    mainEntity: items.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: Array.isArray(entry.answer) ? entry.answer.join(' ') : entry.answer,
      },
    })),
  };
}

export function buildContactPage({ path, name, description, lang }) {
  return {
    '@type': 'ContactPage',
    '@id': ids.webpage(path),
    url: canonicalUrl(path),
    name,
    description,
    isPartOf: ref(ids.website),
    about: ref(ids.organization),
    mainEntity: ref(ids.organization),
    inLanguage: lang,
  };
}

export function buildCreativeWork({
  idx,
  name,
  description,
  image,
  keywords,
  isPublished,
  liveUrl,
  lang,
}) {
  return {
    '@type': 'CreativeWork',
    '@id': ids.project(idx),
    name,
    description,
    url: canonicalUrl(`/project/${idx}`),
    image,
    keywords,
    creator: ref(ids.person),
    publisher: ref(ids.organization),
    // Schema.org allows free-text lifecycle status; no formal enum exists.
    // Only set for the one project actually flagged isPublished: false in
    // projectsList.js -- never invented for the rest.
    creativeWorkStatus: isPublished ? undefined : 'In development',
    // Only a genuinely external, currently-live deployment of this exact
    // work qualifies as sameAs -- not the internal /project/:id page itself.
    sameAs: isPublished && liveUrl && /^https?:\/\//.test(liveUrl) ? liveUrl : undefined,
    mainEntityOfPage: ref(ids.projectPage(idx)),
    inLanguage: lang,
  };
}

export function buildProjectWebPage({ idx, name, description, image, lang }) {
  return {
    '@type': 'WebPage',
    '@id': ids.projectPage(idx),
    url: canonicalUrl(`/project/${idx}`),
    name,
    description,
    isPartOf: ref(ids.website),
    about: ref(ids.project(idx)),
    mainEntity: ref(ids.project(idx)),
    primaryImageOfPage: image,
    inLanguage: lang,
  };
}

// priceText as it's literally displayed, e.g. "From €490 + VAT 25.5%" or
// a genuinely fixed "€590 + VAT 25.5%" -- and their ru/fi equivalents
// ("От €490...", "Alkaen €490..."), since packageDetails.json now supplies
// this text pre-translated per language. Returns undefined when it can't
// confidently parse a number back out -- callers must not fabricate a
// fallback price in that case.
// \b relies on JS's ASCII-only \w, which doesn't include Cyrillic letters --
// against "От €490" it sees non-word char on both sides of the boundary
// position and never matches. Anchoring on the following whitespace instead
// works for all three scripts equally.
const FROM_PREFIX = /^(from|от|alkaen)(\s|$)/i;

export function parsePriceText(priceText) {
  if (!priceText) return undefined;
  const match = String(priceText).match(/(\d[\d.,]*)/);
  if (!match) return undefined;
  const amount = Number(match[1].replace(/,/g, ''));
  if (Number.isNaN(amount)) return undefined;
  const isFrom = FROM_PREFIX.test(String(priceText).trim());
  return { amount, isFrom };
}

export function buildOffer({ priceText, url }) {
  const parsed = parsePriceText(priceText);
  if (!parsed) return undefined;
  if (parsed.isFrom) {
    // "From €490" is a starting price, not a guaranteed final cost --
    // minPrice says exactly that, `price` would not.
    return {
      '@type': 'Offer',
      url,
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        minPrice: parsed.amount,
        priceCurrency: 'EUR',
        valueAddedTaxIncluded: false,
      },
      availability: 'https://schema.org/InStock',
    };
  }
  return {
    '@type': 'Offer',
    url,
    price: parsed.amount,
    priceCurrency: 'EUR',
    valueAddedTaxIncluded: false,
    availability: 'https://schema.org/InStock',
  };
}

export function buildService({ slug, name, description, priceText, lang }) {
  return {
    '@type': 'Service',
    '@id': ids.service(slug),
    name,
    description,
    provider: ref(ids.organization),
    areaServed: SITE.areaServed,
    serviceType: 'Web design and development',
    url: canonicalUrl(`/package/${slug}`),
    offers: buildOffer({ priceText, url: canonicalUrl(`/package/${slug}`) }),
    inLanguage: lang,
  };
}

export function buildPackageWebPage({ slug, name, description, lang }) {
  return {
    '@type': 'WebPage',
    '@id': ids.servicePage(slug),
    url: canonicalUrl(`/package/${slug}`),
    name,
    description,
    isPartOf: ref(ids.website),
    about: ref(ids.service(slug)),
    mainEntity: ref(ids.service(slug)),
    inLanguage: lang,
  };
}